import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  makeStyles,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core";
import { useForm, Controller } from "react-hook-form";
import apiHelper from "../../../util/ApiHelper";
import { useLoading } from "../../../util/LoadingProvider";
import { useBulkFetch } from "../../../util/useFetch";
import SlideSection from "./components/SlideSection";
import BottomBtnSection from "./components/BottomBtnSection";
import SideHomeSection from "./components/SideHomeSection";
import RotationBannerSection from "./components/RotationBannerSection";
import ColorPicker from "./components/ColorPicker";
import PostSection from "./components/PostSection";
import { toast } from "react-toastify";

const frontendUrl = process.env.REACT_APP_HKNT_WEB_HOSTNAME;

const useStyles = makeStyles((theme) => ({
  posts: {
    maxHeight: "405px",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    "& svg": {
      margin: theme.spacing(1.5),
    },
    "& hr": {
      margin: theme.spacing(0, 0.5),
    },
  },
  postsControl: {
    width: "5%",
    height: "405px",
    borderRight: `1px solid ${theme.palette.divider}`,
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  section: {
    background: "#FFF",
    padding: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  subsectionHeader: {
    paddingTop: 0,
    paddingBottom: 0,
    marginTop: 0,
  },
  editBtn: {
    marginLeft: "10px",
    padding: "0px 20px",
  },
  closeBtn: {
    color: "#BCBCBC",
  },
  tabpanel: {
    background: "#FFFFFF",
  },
  textInput: {
    backgroundColor: "white",
    width: "100%",
    marginBottom: "12px",

    "& input": {
      fontSize: "14px",
    },
  },
  fileInput: {
    width: "100%",
    marginBottom: "12px",

    "& input": {
      fontSize: "13px",
    },
  },
  buttonGroup: {
    marginTop: "20px",
    textAlign: "right",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  box: {
    padding: 0,
  },
  innerBox: {
    marginBottom: theme.spacing(4),
  },
  slideContainer: {
    marginBottom: "0px",
    paddingTop: "1px",
    paddingBottom: "1px",
  },
  addSlideBtn: {
    paddingTop: 0,
    marginBottom: 0,
    paddingBottom: 0,
  },
  "&::placeholder": {
    color: "grey",
  },
}));

function TabPanel(props) {
  const classes = useStyles();
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nav-tabpanel-${index}`}
      aria-labelledby={`nav-tab-${index}`}
      {...other}
    >
      <Box p={3} className={classes.box}>
        {children}
      </Box>
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `nav-tab-${index}`,
    "aria-controls": `nav-tabpanel-${index}`,
  };
}

const orderArray = (array) => {
  return array.map((item, idx) => ({ ...item, seq: idx }));
};

const defaultFormValues = {
  banners: [],
  news: [],
  social: [],
  slides: [],
  rotationBanners: [],
  settings: [],
  status: null,
};

export default function HomePage({ disabled, ...props }) {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(0);
  const [activeLang, setActiveLang] = useState("en");
  const [selectedNews, setSelectedNews] = useState([]);
  const [defaultValues, setDefaultValues] = useState(defaultFormValues);
  const { setLoading } = useLoading();

  const { control, handleSubmit, getValues, setValue, reset } = useForm({
    defaultFormValues,
  });

  const data = useBulkFetch(
    [
      apiHelper.hknt.hkntGetHomepage,
      () => apiHelper.hknt.hkntGetPagesByCategory("news"),
    ],
    setLoading
  );

  useEffect(() => {
    if (data) {
      const formValues = {
        banners: data[0].data.banners,
        news: data[0].data.news,
        social: data[0].data.social,
        slides: data[0].data.slides,
        settings: data[0].data.settings,
        rotationBanners: data[0].data.rotationBanners,
        status: null,
      };
      reset(formValues);
      setDefaultValues(formValues);
    }
  }, [data, reset]);

  const getActiveLang = () => {
    return activeLang;
  };

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const onSubmit = (status) => async (formData, e) => {
    setLoading(true);
    console.log(formData.rotationBanners);
    const homepageData = {
      banners: formData.banners,
      news: selectedNews,
      slides: formData.slides,
      social: formData.social,
      rotationBanners:
        formData?.rotationBanners
          ? orderArray(formData.rotationBanners)
          : [],
      settings: [{ key: "bgColor", value: formData.bgColor || "#7a0000" }], // #7a0000 is the default color
      status,
    };
    try {
      const result = await apiHelper.hknt.hkntUpdateHomepage(homepageData);
      if (homepageData.status === "PREVIEW") {
        window.open(frontendUrl + "/preview/homepage", "_blank");
      } else {
        console.log(result);
        toast.dark("Your changes is saved");
      }
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  };

  const onError = (errors, e) => {
    console.log(errors, e);
  };

  return (
    <div className={classes.content}>
      <Typography variant="h4" gutterBottom>
        Page &gt; Edit Homepage
      </Typography>
      <Tabs
        value={activeTab}
        onChange={handleChange}
        aria-label="nav tabs example"
        TabIndicatorProps={{ style: { top: "0", background: "#306F9D" } }}
      >
        <Tab
          label="English"
          {...a11yProps(0)}
          onClick={() => {
            setActiveLang("en");
          }}
        />
        <Tab
          label="繁體中文"
          {...a11yProps(1)}
          onClick={() => {
            setActiveLang("tc");
          }}
        />
      </Tabs>
      <form>
        <Grid className={classes.section}>
          <fieldset disabled={disabled} className="fieldset">
            <TabPanel value={activeTab} index={0}>
              <Box className={classes.subsectionHeader}>
                <Grid item xs={12}>
                  <Box ml={2}>
                    <h4>
                      <small>Home Carousel</small>
                    </h4>
                  </Box>
                </Grid>
              </Box>
            </TabPanel>
            <TabPanel value={activeTab} index={1}>
              <Box className={classes.subsectionHeader}>
                <Grid item xs={12}>
                  <Box ml={2}>
                    <h4>
                      <small>Home Carousel</small>
                    </h4>
                  </Box>
                </Grid>
              </Box>
            </TabPanel>
            <Box className={classes.innerBox + " " + classes.addSlideBtn}>
              <SlideSection
                defaultValues={defaultValues.slides}
                getValues={getValues}
                setValue={setValue}
                control={control}
                lang={() => getActiveLang()}
                disabled={disabled}
              />
            </Box>
            <Box className={classes.subsectionHeader}>
              <Grid item xs={12}>
                <Box ml={2}>
                  <h4>
                    <small>Bottom Home Carousel Buttons</small>
                  </h4>
                </Box>
              </Grid>
            </Box>
            <Box className={classes.innerBox}>
              <BottomBtnSection
                defaultValues={defaultValues.social}
                Controller={Controller}
                control={control}
                getValues={getValues}
                setValue={setValue}
                lang={() => getActiveLang()}
                disabled={disabled}
              />
            </Box>
            <Box className={classes.subsectionHeader}>
              <Grid item xs={12}>
                <Box ml={2}>
                  <h4>
                    <small>Side Home Carousel Banners</small>
                  </h4>
                </Box>
              </Grid>
            </Box>
            <Box className={classes.innerBox}>
              <SideHomeSection
                defaultValues={defaultValues.banners}
                Controller={Controller}
                control={control}
                getValues={getValues}
                setValue={setValue}
                lang={() => getActiveLang()}
                disabled={disabled}
              />
            </Box>
          </fieldset>
        </Grid>

        <Grid className={classes.section}>
          <Box className={classes.subsectionHeader}>
            <Grid item xs={12}>
              <Box ml={2}>
                <h4 style={{ marginTop: 0 }}>
                  <small>Rotation Banner</small>
                </h4>
              </Box>
            </Grid>
          </Box>
          <fieldset disabled={disabled} className="fieldset">
            <Box className={classes.innerBox}>
              <RotationBannerSection
                defaultValues={defaultValues.rotationBanners}
                Controller={Controller}
                control={control}
                getValues={getValues}
                setValue={setValue}
                disabled={disabled}
              />
            </Box>
          </fieldset>
          <Box className={classes.subsectionHeader}>
            <Grid item xs={12}>
              <Box ml={2}>
                <h4>
                  <small>What's New background color</small>
                </h4>
              </Box>
            </Grid>
          </Box>
          <Box className={classes.innerBox} ml={2}>
            <ColorPicker
              getValues={getValues}
              setValue={setValue}
              defaultValues={defaultValues.settings}
              // settingsdata={data[0].data.settings}
              disabled={disabled}
            />
          </Box>
          <Box className={classes.subsectionHeader}>
            <Grid item xs={12}>
              <Box ml={2}>
                <h4>
                  <small>What's New Posts List</small>
                </h4>
              </Box>
            </Grid>
          </Box>
          <Box className={classes.innerBox}>
            {data && data[0] && data[1] && (
              <PostSection
                setSelectedNews={setSelectedNews}
                selecteddata={data[0].data.news}
                whatsnewsdata={data[1].data.content}
                classes={classes}
                disabled={disabled}
              />
            )}
          </Box>
        </Grid>
        {disabled || (
          <div className={classes.buttonGroup}>
            <Button
              type="button"
              style={{ marginRight: "20px" }}
              onClick={handleSubmit(onSubmit("PREVIEW"), onError)}
            >
              Preview
            </Button>
            <Button
              type="button"
              variant="contained"
              disableElevation
              style={{
                color: "white",
                background: "#306F9D",
                marginRight: "5px",
                padding: "9px 16px",
              }}
              onClick={handleSubmit(onSubmit("PUBLISHED"), onError)}
            >
              Save Changes
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
